import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import ConvertInlineBar from "./ConvertInlineBar";
import { formatDuration, resolveConvertPercent } from "../hooks/useConvertProgress";

function epLabel(v) {
  const s = v.season_number != null ? Number(v.season_number) : null;
  const e = v.episode_number != null ? Number(v.episode_number) : null;
  if (s != null && e != null) return `T${String(s).padStart(2, "0")}E${String(e).padStart(2, "0")}`;
  if (e != null) return `E${String(e).padStart(2, "0")}`;
  return `#${v.id}`;
}

function seasonOf(v) {
  const n = v?.season_number;
  if (n == null || Number.isNaN(Number(n))) return 1;
  return Number(n);
}

function sortEpisodes(list) {
  return [...list].sort((a, b) => {
    const sa = seasonOf(a);
    const sb = seasonOf(b);
    if (sa !== sb) return sa - sb;
    const ea = a.episode_number ?? 0;
    const eb = b.episode_number ?? 0;
    if (ea !== eb) return ea - eb;
    return a.id - b.id;
  });
}

function pickInitialSeason(seasons, episodes) {
  if (!seasons.length) return 1;
  const active = episodes.find(
    (v) => v.status === "processing" || v.status === "queued"
  );
  if (active) return seasonOf(active);
  return seasons[0];
}

export default function SeriesEpisodesModal({
  token,
  seriesTitle,
  episodes,
  onClose,
  onEdit,
  onEditSeries,
  onReprocess,
  onRemove,
}) {
  const sorted = useMemo(() => sortEpisodes(episodes || []), [episodes]);

  const seasons = useMemo(() => {
    const set = new Set(sorted.map(seasonOf));
    return [...set].sort((a, b) => a - b);
  }, [sorted]);

  const [season, setSeason] = useState(() => pickInitialSeason(seasons, sorted));
  const [progressMap, setProgressMap] = useState({});

  useEffect(() => {
    if (!seasons.length) return;
    if (!seasons.includes(season)) {
      setSeason(pickInitialSeason(seasons, sorted));
    }
  }, [seasons, season, sorted]);

  const seasonEpisodes = useMemo(
    () => sorted.filter((v) => seasonOf(v) === Number(season)),
    [sorted, season]
  );

  const convertingIds = useMemo(
    () =>
      seasonEpisodes
        .filter((v) => v.status === "queued" || v.status === "processing")
        .map((v) => v.id),
    [seasonEpisodes]
  );

  const convertingKey = convertingIds.join(",");

  useEffect(() => {
    if (!token || !convertingIds.length) {
      setProgressMap({});
      return undefined;
    }
    let cancelled = false;
    let timer;
    const ids = convertingIds;

    async function poll() {
      const next = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            next[id] = await api(`/api/videos/${id}/convert-progress`, { token });
          } catch (_) {
            /* ignore */
          }
        })
      );
      if (!cancelled) setProgressMap((prev) => ({ ...prev, ...next }));
      if (!cancelled && ids.length) {
        timer = window.setTimeout(poll, 1000);
      }
    }

    poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [token, convertingKey]);

  const readyN = sorted.filter((v) => v.status === "ready").length;
  const failedN = sorted.filter((v) => v.status === "failed").length;
  const active =
    sorted.find((v) => v.status === "processing") ||
    sorted.find((v) => v.status === "queued");

  const seasonReady = seasonEpisodes.filter((v) => v.status === "ready").length;
  const seasonFailed = seasonEpisodes.filter((v) => v.status === "failed").length;

  function seasonTabMeta(s) {
    const eps = sorted.filter((v) => seasonOf(v) === s);
    const busy = eps.some(
      (v) => v.status === "queued" || v.status === "processing"
    );
    const failed = eps.some((v) => v.status === "failed");
    return { count: eps.length, busy, failed };
  }

  return createPortal(
    <div
      className="convert-modal-backdrop series-eps-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="series-eps-modal">
        <header className="series-eps-head">
          <div>
            <p className="brand">Casasm</p>
            <h2>{seriesTitle || "Serie"}</h2>
            <p className="muted">
              {sorted.length} capítulo{sorted.length === 1 ? "" : "s"}
              {seasons.length > 1 ? ` · ${seasons.length} temporadas` : ""}
              {readyN ? ` · ${readyN} listos` : ""}
              {failedN ? ` · ${failedN} con error` : ""}
            </p>
          </div>
          <div className="series-eps-head-actions">
            {typeof onEditSeries === "function" ? (
              <button type="button" onClick={() => onEditSeries()}>
                Editar ficha
              </button>
            ) : null}
            <button type="button" className="ghost series-eps-close" onClick={() => onClose?.()}>
              Cerrar
            </button>
          </div>
        </header>

        {active ? (
          <div className="series-eps-active">
            <p className="series-eps-active-title">
              Convirtiendo ahora: <strong>{epLabel(active)}</strong>
              {seasonOf(active) !== Number(season) ? (
                <button
                  type="button"
                  className="ghost series-eps-jump"
                  onClick={() => setSeason(seasonOf(active))}
                >
                  Ir a temp. {seasonOf(active)}
                </button>
              ) : null}
            </p>
            <ConvertInlineBar token={token} videoId={active.id} enabled />
          </div>
        ) : null}

        {seasons.length > 1 ? (
          <div className="series-eps-tabs" role="tablist" aria-label="Temporadas">
            {seasons.map((s) => {
              const meta = seasonTabMeta(s);
              return (
                <button
                  key={s}
                  type="button"
                  role="tab"
                  aria-selected={Number(season) === s}
                  className={`series-eps-tab${Number(season) === s ? " active" : ""}`}
                  onClick={() => setSeason(s)}
                >
                  Temp. {s}
                  <span className="series-eps-tab-count">{meta.count}</span>
                  {meta.busy ? <span className="series-eps-tab-dot is-busy" /> : null}
                  {!meta.busy && meta.failed ? (
                    <span className="series-eps-tab-dot is-fail" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="series-eps-season-meta muted">
          Temporada {season}: {seasonEpisodes.length} capítulo
          {seasonEpisodes.length === 1 ? "" : "s"}
          {seasonReady ? ` · ${seasonReady} listos` : ""}
          {seasonFailed ? ` · ${seasonFailed} con error` : ""}
        </div>

        <div className="series-eps-list" role="tabpanel">
          {seasonEpisodes.length ? (
            seasonEpisodes.map((v) => {
              const prog = progressMap[v.id];
              const converting = v.status === "queued" || v.status === "processing";
              let elapsed = prog?.elapsed;
              if (prog?.started_at && converting) {
                elapsed = Math.max(0, Date.now() / 1000 - Number(prog.started_at));
              }
              const percent = resolveConvertPercent(prog, { status: v.status });
              return (
                <article key={v.id} className="series-eps-item">
                  <div className="series-eps-item-main">
                    {v.cover_url ? (
                      <img className="admin-thumb series-eps-thumb" src={v.cover_url} alt="" />
                    ) : (
                      <div className="admin-thumb fallback series-eps-thumb">—</div>
                    )}
                    <div>
                      <strong>
                        {epLabel(v)}
                        <span className="muted series-eps-id"> #{v.id}</span>
                      </strong>
                      <p className="muted series-eps-title">{v.title}</p>
                      {converting ? (
                        <div className="series-eps-mini">
                          <div className="convert-bar convert-inline-bar">
                            <span style={{ width: `${percent}%` }} />
                          </div>
                          <span className="muted">
                            {percent}%
                            {elapsed != null ? ` · ${formatDuration(elapsed)}` : ""}
                            {prog?.message ? ` · ${prog.message}` : ""}
                          </span>
                        </div>
                      ) : null}
                      {v.status === "failed" && v.error_message ? (
                        <p className="error series-eps-err">{v.error_message}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="series-eps-item-side">
                    <span className={`badge badge-${v.status}`}>{v.status}</span>
                    <div className="actions">
                      <button type="button" onClick={() => onEdit?.(v)}>
                        Editar
                      </button>
                      <button type="button" onClick={() => onReprocess?.(v.id)}>
                        Reprocesar
                      </button>
                      <button type="button" className="danger" onClick={() => onRemove?.(v.id)}>
                        Borrar
                      </button>
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <p className="muted catalog-status">No hay capítulos en esta temporada.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
